import Pages from "src/constants/pages.enum";
import { UserAppRole } from "src/constants/userRoles.enum";
import { type LocationModel } from "src/types/location";
import LocationType from "src/types/LocationType";

export function getMinTreeDepth(locations: LocationModel[]): number {
  return Math.min(...locations.map((location) => location.PathId.split("/").length));
}

export function getLocationsWithTreeDepth(locations: LocationModel[], treeLevel: number): LocationModel[] {
  return locations.filter((location) => location.PathId.split("/").length === treeLevel);
}

export function getLocationTypeFromLocation(location: LocationModel): LocationType {
  return LocationType[location.Type as keyof typeof LocationType];
}

function doesLocationOfTypeExistInLocation(locations: LocationModel[] | undefined, locationType: LocationType): boolean {
  if (!locations) {
    return false;
  }
  return locations.some((location) => getLocationTypeFromLocation(location) === locationType);
}

function getFirstLocationChildren(locations: LocationModel[]): LocationModel[] {
  const minDepth = getMinTreeDepth(locations);
  const firstDepthLocations = getLocationsWithTreeDepth(locations, minDepth);
  if (firstDepthLocations.length === 1 || minDepth === 1) {
    return [...firstDepthLocations, ...getLocationsWithTreeDepth(locations, minDepth + 1)].filter((item) => item.ParentId !== null);
  }
  return firstDepthLocations.filter((item) => item.ParentId !== null);
}

function getChildTypesByParentId(locations: LocationModel[]): Map<string, Set<LocationType>> {
  const childTypesByParentId = new Map<string, Set<LocationType>>();
  for (const location of locations) {
    if (!location.ParentId) continue;
    const types = childTypesByParentId.get(location.ParentId) ?? new Set<LocationType>();
    types.add(getLocationTypeFromLocation(location));
    childTypesByParentId.set(location.ParentId, types);
  }
  return childTypesByParentId;
}

function getLocationsWithChildrenOfLocationType(
  parentLocations: LocationModel[],
  childTypesByParentId: Map<string, Set<LocationType>>,
  locationType: LocationType
): LocationModel[] {
  return parentLocations.filter((location) => childTypesByParentId.get(location.Id)?.has(locationType) ?? false);
}

export function getAvailablePagesBasedOnLocationStructure(locations: LocationModel[], userAppRoles: string[]): Pages[] {
  const availablePages: Pages[] = [];

  const doesMallExist: boolean = doesLocationOfTypeExistInLocation(locations, LocationType.Mall);
  const doesShopExist: boolean = doesLocationOfTypeExistInLocation(locations, LocationType.Shop);
  const doesStationExist: boolean = doesLocationOfTypeExistInLocation(locations, LocationType.Station);

  const firstLocationChildren = getFirstLocationChildren(locations);
  const childTypesByParentId = getChildTypesByParentId(locations);
  const firstLocationChildrenWithMallChilds = getLocationsWithChildrenOfLocationType(
    firstLocationChildren,
    childTypesByParentId,
    LocationType.Mall
  );
  const firstLocationChildrenWithShopChilds = getLocationsWithChildrenOfLocationType(
    firstLocationChildren,
    childTypesByParentId,
    LocationType.Shop
  );
  const firstLocationChildrenWithStationChilds = getLocationsWithChildrenOfLocationType(
    firstLocationChildren,
    childTypesByParentId,
    LocationType.Station
  );

  if (doesMallExist && userAppRoles.includes(UserAppRole.MallViewer)) {
    if (firstLocationChildrenWithMallChilds.length > 0) {
      availablePages.push(Pages.CenterRegionDashboard);
    }
    availablePages.push(Pages.CenterDashboard);
  }

  if (doesShopExist && userAppRoles.includes(UserAppRole.ShopViewer)) {
    if (firstLocationChildrenWithShopChilds.length > 0) {
      availablePages.push(Pages.ShopRegionDashboard);
    }
    availablePages.push(Pages.ShopDashboard);
  }

  if (doesStationExist && userAppRoles.includes(UserAppRole.StationViewer)) {
    if (firstLocationChildrenWithStationChilds.length > 0) {
      availablePages.push(Pages.StationRegionDashboard);
    }
    availablePages.push(Pages.StationDashboard);
  }

  if (userAppRoles.includes(UserAppRole.InsightViewer)) {
    availablePages.push(Pages.InsightsPage);
  }

  if (userAppRoles.includes(UserAppRole.DocumentViewer)) {
    availablePages.push(Pages.DocumentPage);
  }

  return availablePages;
}
